import React, { useEffect, useState, useRef } from "react";
import SidebarNav from "../sidebar";
import { useParams, useHistory, Link } from "react-router-dom";
import { getOrderById, getCoinDcxOrderById, cancelAdminOrder } from "../../../service/api";
import { Tag, Spin, Tooltip } from "antd";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Backdrop from "@mui/material/Backdrop";
import { Spinner } from "react-bootstrap";
import { useReactToPrint } from "react-to-print";
import { downloadCSV } from "../../../config/downloadUtils";
import { FiArrowLeft, FiDownload, FiPrinter, FiXCircle } from "react-icons/fi";
import moment from "moment";
import { toast } from "react-toastify";

const STATUS_COLORS = {
  COMPLETED: { bg: "#e6f7ee", text: "#1a7a4a", border: "#b7ebce" },
  FILLED:    { bg: "#e6f7ee", text: "#1a7a4a", border: "#b7ebce" },
  PENDING:   { bg: "#fff8e6", text: "#9a6700", border: "#ffe58f" },
  PARTIALLY_FILLED: { bg: "#fff8e6", text: "#9a6700", border: "#ffe58f" },
  CANCELLED: { bg: "#fff1f0", text: "#cf1322", border: "#ffa39e" },
  CANCELED:  { bg: "#fff1f0", text: "#cf1322", border: "#ffa39e" },
  REJECTED:  { bg: "#fff1f0", text: "#cf1322", border: "#ffa39e" },
  OPEN:      { bg: "#e6f4ff", text: "#0958d9", border: "#91caff" },
};

const ANT_STATUS_COLORS = {
  COMPLETED: "green", FILLED: "green",
  PENDING: "orange", PARTIALLY_FILLED: "orange",
  CANCELLED: "red", CANCELED: "red", REJECTED: "red",
  OPEN: "blue",
};

const formatDateTime = (val) =>
  val ? moment(val).format("DD/MM/YYYY HH:mm:ss") : "—";

const formatCdxTs = (ts) => {
  if (!ts) return "—";
  const m = typeof ts === "string" ? moment(ts) : moment(Number(ts));
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm:ss") : String(ts);
};

// ── small reusable pieces ────────────────────────────────────────────────────

const InfoRow = ({ label, children, mono }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "9px 0", borderBottom: "1px solid #f0f0f0" }}>
    <span style={{ fontSize: 13, color: "#8c8c8c", fontWeight: 500, minWidth: 140 }}>{label}</span>
    <span style={{ fontSize: 13, color: "#262626", textAlign: "right", fontFamily: mono ? "monospace" : undefined, wordBreak: "break-all", maxWidth: "60%" }}>
      {children ?? "—"}
    </span>
  </div>
);

const Card = ({ title, icon, children, style }) => (
  <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 10, marginBottom: 20, overflow: "hidden", ...style }}>
    {title && (
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 8 }}>
        {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
        <span style={{ fontSize: 14, fontWeight: 600, color: "#262626" }}>{title}</span>
      </div>
    )}
    <div style={{ padding: "4px 20px 12px" }}>{children}</div>
  </div>
);

const StatCard = ({ label, value, color }) => (
  <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 10, padding: "16px 20px", flex: "1 1 180px", minWidth: 0 }}>
    <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 6, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 700, color: color || "#262626" }}>{value ?? "—"}</div>
  </div>
);

const StatusPill = ({ status }) => {
  if (!status) return <span style={{ color: "#bbb" }}>—</span>;
  const key = status.toUpperCase();
  const c = STATUS_COLORS[key] || { bg: "#f5f5f5", text: "#595959", border: "#d9d9d9" };
  return (
    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
      {key}
    </span>
  );
};

const TooltipLabel = ({ label, tip }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
    {label}
    <Tooltip title={tip} placement="top">
      <span style={{ cursor: "help", color: "#aab4be", fontSize: 10, border: "1px solid #d9d9d9", borderRadius: "50%", width: 14, height: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0 }}>?</span>
    </Tooltip>
  </span>
);

const normalizeExchangeData = (raw, exchange, fromSym, toSym) => {
  const isKuCoin = exchange === "kucoin";
  const feeUnit = (isKuCoin ? raw.feeCurrency : raw.fee_currency) || toSym;
  const fields = [
    { label: "Exchange Order ID", mono: true,    value: isKuCoin ? (raw.orderId || raw.id) : raw.id },
    { label: "Client Order ID",   mono: true,    value: isKuCoin ? raw.clientOid : raw.client_order_id },
    { label: "Pair",                             value: isKuCoin ? raw.symbol : raw.market },
    { label: "Side",              type: "side",  value: raw.side },
    { label: "Order Type",                       value: isKuCoin ? raw.type : (raw.order_type === "limit_order" ? "limit" : raw.order_type === "market_order" ? "market" : raw.order_type) },
    { label: "Status",            type: "status", value: isKuCoin ? (() => {
      if (raw.isActive === true) return "open";
      const dealSz  = parseFloat(raw.dealSize  ?? '0') || 0;
      const totalSz = parseFloat(raw.size      ?? '0') || 0;
      const remainSz = totalSz - dealSz;
      if (dealSz > 0 && remainSz <= 0) return "filled";
      if (dealSz > 0 && remainSz > 0)  return "partially_filled";
      return "cancelled";
    })() : (() => {
      const s = raw.status ?? '';
      if (['cancel', 'partially_cancelled', 'rejected'].includes(s)) return 'cancelled';
      return s || null;
    })() },
    { label: "Price",             unit: toSym,   value: isKuCoin ? raw.price : raw.price_per_unit },
    { label: "Avg Fill Price",    unit: toSym,   value: isKuCoin ? null : raw.avg_price },
    { label: "Total Quantity",    unit: fromSym, value: isKuCoin ? raw.size : raw.total_quantity },
    { label: "Filled Quantity",   unit: fromSym, value: isKuCoin ? raw.dealSize : (() => { const t = parseFloat(raw.total_quantity); const r = parseFloat(raw.remaining_quantity); return (!isNaN(t) && !isNaN(r)) ? String(+(t - r).toPrecision(10)) : null; })() },
    { label: "Remaining Quantity", unit: fromSym, value: isKuCoin ? (raw.remainSize ?? null) : raw.remaining_quantity },
    { label: "Deal Value",        unit: toSym,   value: isKuCoin ? raw.dealFunds : null },
    { label: "Exchange Fee",      unit: feeUnit, tooltip: `Fee charged by ${isKuCoin ? "KuCoin" : "CoinDCX"} to execute this order`, value: isKuCoin ? raw.fee : (raw.fee_amount || raw.fee) },
    { label: "Fee Currency",                     value: feeUnit },
    { label: "Time in Force",                    value: isKuCoin ? raw.timeInForce : raw.time_in_force },
    { label: "Stop Price",        unit: toSym,   value: isKuCoin ? (raw.stopPrice || null) : (raw.stop_price != null && raw.stop_price !== 0 ? raw.stop_price : null) },
    { label: "Created At",        type: "time",  value: isKuCoin ? raw.createdAt : raw.created_at },
    { label: "Updated At",        type: "time",  value: isKuCoin ? raw.updatedAt : raw.updated_at },
  ];
  return fields.filter(f =>
    f.type === "side" || f.type === "status" ||
    (f.value != null && f.value !== "" && f.value !== "0" && f.value !== 0)
  );
};

// ── main component ───────────────────────────────────────────────────────────

const OrderDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const printRef = useRef();

  const [data, setData] = useState(null);
  const [exchangeData, setExchangeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exchangeLoading, setExchangeLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Order-${id}`,
  });

  const handleDownloadCSV = () => {
    if (!data) return;
    const status = data?.orderId?.orderStatus || data?.orderStatus || "";
    const rows = [
      { field: "Order ID", value: data?._id || "" },
      { field: "User Name", value: `${data?.user?.firstName || ""} ${data?.user?.lastName || ""}`.trim() },
      { field: "Email", value: data?.user?.email || "" },
      { field: "Phone", value: data?.user?.phone || "" },
      { field: "Order Type", value: data?.orderType || "" },
      { field: "Order Status", value: status },
      { field: "From Coin", value: data?.fromCoin?.name || "" },
      { field: "From Symbol", value: data?.fromCoin?.symbol || "" },
      { field: "To Coin", value: data?.toCoin?.name || "" },
      { field: "To Symbol", value: data?.toCoin?.symbol || "" },
      { field: "Amount", value: data?.amount != null ? `${data.amount} ${data?.toCoin?.symbol || ""}` : "" },
      { field: "Price", value: data?.price != null ? `${data.price} ${data?.toCoin?.symbol || ""}` : "" },
      { field: "Quantity", value: data?.quantity != null ? `${data.quantity} ${data?.fromCoin?.symbol || ""}` : "" },
      {
        field: "Total Quantity",
        value:
          (data?.totalQuantity ?? data?.totalQty ?? data?.quantity) != null
            ? `${data?.totalQuantity ?? data?.totalQty ?? data?.quantity} ${data?.fromCoin?.symbol || ""}`.trim()
            : "",
      },
      {
        field: "Remaining Quantity",
        value:
          (data?.remainingQuantity ?? data?.pendingQuantity) != null
            ? `${data?.remainingQuantity ?? data?.pendingQuantity} ${data?.fromCoin?.symbol || ""}`.trim()
            : "",
      },
      {
        field: "Filled Quantity",
        value:
          data?.filledQuantity != null
            ? `${data.filledQuantity} ${data?.fromCoin?.symbol || ""}`.trim()
            : "",
      },
      { field: "TDS", value: data?.tds != null ? String(data.tds) : "" },
      { field: "Fee", value: data?.fee != null ? `${Number(data.fee).toPrecision(5)} ${data?.toCoin?.symbol || ""}` : "" },
      { field: "Created At", value: formatDateTime(data?.createdAt) },
      { field: "Updated At", value: formatDateTime(data?.updatedAt) },
    ];
    const cols = [
      { key: "field", title: "Field", render: (r) => r.field },
      { key: "value", title: "Value", render: (r) => r.value },
    ];
    downloadCSV(rows, cols, `order-${id}-${moment().format("YYYY-MM-DD")}.csv`);
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getOrderById(id)
      ?.then((res) => {
        if (res?.status) {
          const orderData = res.data;
          setData(orderData);
          if (orderData?.coinDcxOrderId) {
            setExchangeLoading(true);
            // Pass the MongoDB _id so the backend can resolve exchange + order ID
            getCoinDcxOrderById(id)
              .then((exRes) => {
                const ex = exRes?.data ?? exRes;
                if (ex && typeof ex === "object" && Object.keys(ex).length > 0) {
                  setExchangeData(ex);
                }
              })
              .catch(() => {})
              .finally(() => setExchangeLoading(false));
          }
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  const status = data?.orderId?.orderStatus || data?.orderStatus;
  const pair = data?.fromCoin?.symbol && data?.toCoin?.symbol
    ? `${data.fromCoin.symbol} / ${data.toCoin.symbol}`
    : "—";

  const handleCancelOrder = () => setShowCancelModal(true);

  const handleCancelConfirm = () => {
    setCancelling(true);
    cancelAdminOrder(id)
      .then((res) => {
        if (res?.status) {
          toast.success(res?.message || "Order cancelled successfully", { toastId: 'detail-cancel-success' });
          setShowCancelModal(false);
          getOrderById(id)?.then((r) => { if (r?.status) setData(r.data); });
        } else {
          toast.error(res?.message || "Failed to cancel order", { toastId: 'detail-cancel-error' });
        }
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message || e?.message || "Something went wrong", { toastId: 'detail-cancel-error' });
      })
      .finally(() => setCancelling(false));
  };

  const exchange = (data?.exchange ?? "coindcx").toLowerCase();
  const exchangeLabel = exchange === "kucoin" ? "KuCoin" : "CoinDCX";
  const exchangeCardStyle = exchange === "kucoin"
    ? { border: "1px solid #d3adf7", background: "#f9f0ff" }
    : { border: "1px solid #d6e4ff", background: "#f0f5ff" };
  const exchangeIcon = exchange === "kucoin" ? "🟣" : "⚡";

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">

          {/* ── Page Header ── */}
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Order Detail</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/admin">Dashboard</Link></li>
                  <li className="breadcrumb-item"><Link to="/admin/order">Orders</Link></li>
                  <li className="breadcrumb-item active">Order Detail</li>
                </ul>
              </div>
              <div className="col-auto d-flex gap-2">
                <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={() => history.goBack()}>
                  <FiArrowLeft /> Back
                </button>
                <button className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1" onClick={handleDownloadCSV} disabled={!data}>
                  <FiDownload /> CSV
                </button>
                <button className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1" onClick={handlePrint} disabled={!data}>
                  <FiPrinter /> Print
                </button>
                {["OPEN", "PARTIALLY_FILLED"].includes(status?.toUpperCase()) && (
                  <button
                    className="btn btn-sm btn-danger d-flex align-items-center gap-1"
                    onClick={handleCancelOrder}
                    disabled={cancelling}
                  >
                    <FiXCircle /> Cancel Order
                  </button>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5"><Spin size="large" /></div>
          ) : (
            <div ref={printRef}>

              {/* ── Summary Stat Cards ── */}
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
                <StatCard label="Status" value={<StatusPill status={status} />} />
                <StatCard
                  label="Order Type"
                  value={data?.orderType
                    ? <Tag color={data.orderType === "BUY" ? "green" : "red"} style={{ fontSize: 13, padding: "2px 10px" }}>{data.orderType}</Tag>
                    : "—"}
                />
                <StatCard
                  label="Pair"
                  value={<span style={{ fontWeight: 700, fontSize: 17 }}>{pair}</span>}
                />
                <StatCard
                  label="Amount"
                  value={data?.amount != null ? `${data.amount} ${data?.toCoin?.symbol || ""}` : "—"}
                  color="#1677ff"
                />
                <StatCard
                  label="Quantity"
                  value={data?.quantity != null ? `${Number(data.quantity).toPrecision(5)} ${data?.fromCoin?.symbol || ""}` : "—"}
                />
                <StatCard
                  label="Created"
                  value={<span style={{ fontSize: 13 }}>{formatDateTime(data?.createdAt)}</span>}
                />
              </div>

              {/* ── Main Grid ── */}
              <div className="row">

                {/* ── LEFT COLUMN ── */}
                <div className={exchangeData ? "col-lg-6" : "col-12"}>

                  {/* User */}
                  <Card title="User" icon="👤">
                    <InfoRow label="Full Name">
                      {[data?.user?.firstName, data?.user?.lastName].filter(Boolean).join(" ") || null}
                    </InfoRow>
                    <InfoRow label="Email">{data?.user?.email}</InfoRow>
                    <InfoRow label="Phone">{data?.user?.phone}</InfoRow>
                  </Card>

                  {/* Order */}
                  <Card title="Order Details" icon="📋">
                    <InfoRow label="Order ID" mono>{data?._id}</InfoRow>
                    <InfoRow label="Status"><StatusPill status={status} /></InfoRow>
                    <InfoRow label="Type">
                      {data?.orderType
                        ? <Tag color={data.orderType === "BUY" ? "green" : "red"}>{data.orderType}</Tag>
                        : null}
                    </InfoRow>
                    <InfoRow label="Amount">
                      {data?.amount != null && data?.toCoin?.symbol ? `${data.amount} ${data.toCoin.symbol}` : null}
                    </InfoRow>
                    <InfoRow label="Price">
                      {data?.price != null && data?.toCoin?.symbol ? `${data.price} ${data.toCoin.symbol}` : null}
                    </InfoRow>
                    <InfoRow label="Quantity">
                      {data?.quantity != null && data?.fromCoin?.symbol ? `${data.quantity} ${data.fromCoin.symbol}` : null}
                    </InfoRow>
                    <InfoRow label="Total Quantity">
                      {(() => {
                        const total =
                          data?.totalQuantity ?? data?.totalQty ?? data?.quantity;
                        return total != null
                          ? `${total} ${data?.fromCoin?.symbol || ""}`.trim()
                          : null;
                      })()}
                    </InfoRow>
                    <InfoRow label="Remaining Quantity">
                      {(() => {
                        const remaining =
                          data?.remainingQuantity ?? data?.pendingQuantity;
                        return remaining != null
                          ? `${remaining} ${data?.fromCoin?.symbol || ""}`.trim()
                          : null;
                      })()}
                    </InfoRow>
                    <InfoRow label="Filled Quantity">
                      {data?.filledQuantity != null
                        ? `${data.filledQuantity} ${data?.fromCoin?.symbol || ""}`.trim()
                        : null}
                    </InfoRow>
                    <InfoRow label={<TooltipLabel label="Platform Fee" tip="0.42% trading fee charged by Paraa on order notional" />}>
                      {data?.platformFee != null ? `${Number(data.platformFee).toPrecision(6)} ${data?.toCoin?.symbol || ""}`.trim() : null}
                    </InfoRow>
                    <InfoRow label={<TooltipLabel label="GST" tip="18% GST levied on platform fee" />}>
                      {data?.platformGst != null ? `${Number(data.platformGst).toPrecision(6)} ${data?.toCoin?.symbol || ""}`.trim() : null}
                    </InfoRow>
                    <InfoRow label={<TooltipLabel label="TDS" tip="1% Tax Deducted at Source on SELL order proceeds. Zero for BUY orders." />}>
                      {data?.platformTds != null ? `${Number(data.platformTds).toPrecision(6)} ${data?.toCoin?.symbol || ""}`.trim() : null}
                    </InfoRow>
                    <InfoRow label={<TooltipLabel label="Exchange Fee" tip={`Fee charged by ${exchangeLabel} to execute this order. May be in a different currency than the pair.`} />}>
                      {data?.exchangeFee != null
                        ? `${Number(data.exchangeFee).toPrecision(6)} ${data?.feeCurrency || data?.toCoin?.symbol || ""}`.trim()
                        : null}
                    </InfoRow>
                    <InfoRow label={<TooltipLabel label="Total Platform Fees" tip="Sum of Platform Fee + GST + TDS (all in quote currency)" />}>
                      {(() => {
                        const pf = Number(data?.platformFee ?? 0);
                        const gst = Number(data?.platformGst ?? 0);
                        const tds = Number(data?.platformTds ?? 0);
                        return data?.platformFee != null || data?.platformGst != null
                          ? `${(pf + gst + tds).toPrecision(6)} ${data?.toCoin?.symbol || ""}`.trim()
                          : null;
                      })()}
                    </InfoRow>
                    <InfoRow label="Created At">{formatDateTime(data?.createdAt)}</InfoRow>
                    <InfoRow label="Updated At">{formatDateTime(data?.updatedAt)}</InfoRow>
                  </Card>

                  {/* Pair */}
                  <Card title="Trading Pair" icon="🔄">
                    <InfoRow label="From Coin">{data?.fromCoin?.name}</InfoRow>
                    <InfoRow label="From Symbol">{data?.fromCoin?.symbol}</InfoRow>
                    <InfoRow label="To Coin">{data?.toCoin?.name}</InfoRow>
                    <InfoRow label="To Symbol">{data?.toCoin?.symbol}</InfoRow>
                    <InfoRow label="Min Trade Amount">
                      {data?.pairId?.minTradeAmount != null ? String(data.pairId.minTradeAmount) : null}
                    </InfoRow>
                    <InfoRow label="Max Trade Amount">
                      {data?.pairId?.maxTradeAmount != null ? String(data.pairId.maxTradeAmount) : null}
                    </InfoRow>
                    <InfoRow label="Current Price">
                      {data?.pairId?.currentPrice != null ? String(data.pairId.currentPrice) : null}
                    </InfoRow>
                  </Card>

                  {/* Cancelled by Admin */}
                  {data?.cancelledByAdmin && (
                    <Card title="Cancelled by Admin" icon="🚫" style={{ border: "1px solid #ffa39e", background: "#fff1f0" }}>
                      <InfoRow label="Cancelled By">{data?.cancelledByAdminName || "—"}</InfoRow>
                      <InfoRow label="Admin Email">{data?.cancelledByAdminEmail || "—"}</InfoRow>
                      <InfoRow label="Admin ID" mono>{data?.cancelledByAdminId || "—"}</InfoRow>
                      <InfoRow label="Cancelled At">{formatDateTime(data?.cancelledAt)}</InfoRow>
                    </Card>
                  )}

                  {/* Exchange Order (orderId populated ref) */}
                  {data?.orderId && typeof data.orderId === "object" && (
                    <Card title="Exchange Order" icon="🏦">
                      {Object.entries(data.orderId)
                        .filter(([key]) => !["__v", "_id"].includes(key))
                        .map(([key, value]) => (
                          <InfoRow
                            key={key}
                            label={key.replace(/_/g, " ").replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim()}
                            mono={key.toLowerCase().includes("id")}
                          >
                            {value != null && value !== "" ? String(value) : null}
                          </InfoRow>
                        ))}
                    </Card>
                  )}
                </div>

                {/* ── RIGHT COLUMN — Exchange (CoinDCX or KuCoin) ── */}
                {(exchangeLoading || exchangeData) && (
                  <div className="col-lg-6">
                    <Card title={`${exchangeLabel} Order`} icon={exchangeIcon} style={exchangeCardStyle}>
                      {exchangeLoading ? (
                        <div className="text-center py-4"><Spin /></div>
                      ) : (
                        normalizeExchangeData(exchangeData, exchange, data?.fromCoin?.symbol || "", data?.toCoin?.symbol || "")
                          .map(({ label, value, mono, unit, type, tooltip }) => {
                            let display;
                            if (type === "side") {
                              display = value
                                ? <Tag color={String(value).toLowerCase() === "buy" ? "green" : "red"}>{String(value).toUpperCase()}</Tag>
                                : null;
                            } else if (type === "status") {
                              display = value
                                ? <Tag color={ANT_STATUS_COLORS[String(value).toUpperCase()] || "default"}>{String(value).toUpperCase()}</Tag>
                                : null;
                            } else if (type === "time") {
                              display = formatCdxTs(value);
                            } else if (value != null) {
                              display = unit ? `${value} ${unit}`.trim() : String(value);
                            } else {
                              display = null;
                            }
                            const labelNode = tooltip
                              ? <TooltipLabel label={label} tip={tooltip} />
                              : label;
                            return (
                              <InfoRow key={label} label={labelNode} mono={mono}>{display}</InfoRow>
                            );
                          })
                      )}
                    </Card>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancel confirmation modal */}
      <Modal
        open={showCancelModal}
        onClose={() => !cancelling && setShowCancelModal(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Fade in={showCancelModal}>
          <Box style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 320, background: "white", borderRadius: "10px", boxShadow: 24, p: 4, outline: "none" }}>
            <div style={{ margin: "10px 10px 6px", textAlign: "center" }}>
              <div style={{ fontSize: "16px", fontWeight: 600, marginBottom: "8px" }}>Cancel Order</div>
              <div style={{ fontSize: "13px", color: "#555", marginBottom: "8px" }}>
                Are you sure you want to cancel this order? This action cannot be undone.
              </div>
              {data && (
                <div style={{ background: "#f8f8f8", borderRadius: "6px", padding: "8px 10px", textAlign: "left", fontSize: "13px" }}>
                  <div><strong>User:</strong> {[data?.user?.firstName, data?.user?.lastName].filter(Boolean).join(" ") || "-"}</div>
                  <div><strong>Pair:</strong> {data?.fromCoin?.symbol && data?.toCoin?.symbol ? `${data.fromCoin.symbol}/${data.toCoin.symbol}` : "-"}</div>
                  <div><strong>Status:</strong> {data?.orderId?.orderStatus || data?.orderStatus || "-"}</div>
                  <div><strong>Type:</strong> {data?.type || "-"} / {data?.orderType || "-"}</div>
                </div>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <button
                className="btn btn-danger"
                disabled={cancelling}
                onClick={handleCancelConfirm}
                style={{ marginTop: "15px", marginBottom: "10px" }}
              >
                {cancelling ? <Spinner size="sm" /> : "Cancel Order"}
              </button>
              <button
                className="btn btn-primary"
                disabled={cancelling}
                onClick={() => setShowCancelModal(false)}
                style={{ marginTop: "15px", marginBottom: "10px" }}
              >
                Keep Order
              </button>
            </div>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default OrderDetail;
